import { useEffect, useRef, useState } from 'react';
type Direction = 'forward' | 'backward' | 'fade' | 'scale-up' | 'scale-down';

// Define the logical order/depth of views
const VIEW_DEPTH: Record<string, number> = {
  home: 0,
  modules: 1,
  progress: 1,
  collection: 1,
  assessment: 1,
  learningLab: 1,
  settings: 1
  // Any sub-views would have depth 2, etc.
};
export function useDirectionalTransition(currentView: string) {
  const previousViewRef = useRef<string>(currentView);
  const [direction, setDirection] = useState<Direction>('fade');
  useEffect(() => {
    const prev = previousViewRef.current;
    const curr = currentView;
    if (prev !== curr) {
      const prevDepth = VIEW_DEPTH[prev] ?? 0;
      const currDepth = VIEW_DEPTH[curr] ?? 0;
      if (curr === 'home' && prev !== 'home') {
        // Going back to home is always backward
        setDirection('backward');
      } else if (prev === 'home' && curr !== 'home') {
        // Going from home to anywhere is forward
        setDirection('forward');
      } else if (currDepth > prevDepth) {
        setDirection('forward');
      } else if (currDepth < prevDepth) {
        setDirection('backward');
      } else {
        // Same depth (e.g. modules to progress) -> crossfade
        setDirection('fade');
      }
      previousViewRef.current = curr;
    }
  }, [currentView]);
  return direction;
}
export const getTransitionVariants = (direction: Direction, isReducedMotion: boolean) => {
  if (isReducedMotion) {
    return {
      initial: {
        opacity: 0
      },
      animate: {
        opacity: 1
      },
      exit: {
        opacity: 0
      }
    };
  }
  switch (direction) {
    case 'forward':
      return {
        initial: {
          opacity: 0,
          x: 20,
          scale: 0.98
        },
        animate: {
          opacity: 1,
          x: 0,
          scale: 1
        },
        exit: {
          opacity: 0,
          x: -20,
          scale: 0.98
        }
      };
    case 'backward':
      return {
        initial: {
          opacity: 0,
          x: -20,
          scale: 0.98
        },
        animate: {
          opacity: 1,
          x: 0,
          scale: 1
        },
        exit: {
          opacity: 0,
          x: 20,
          scale: 0.98
        }
      };
    case 'scale-up':
      return {
        initial: {
          opacity: 0,
          scale: 0.9
        },
        animate: {
          opacity: 1,
          scale: 1
        },
        exit: {
          opacity: 0,
          scale: 1.05
        }
      };
    case 'scale-down':
      return {
        initial: {
          opacity: 0,
          scale: 1.05
        },
        animate: {
          opacity: 1,
          scale: 1
        },
        exit: {
          opacity: 0,
          scale: 0.9
        }
      };
    case 'fade':
    default:
      return {
        initial: {
          opacity: 0,
          y: 10,
          scale: 0.99
        },
        animate: {
          opacity: 1,
          y: 0,
          scale: 1
        },
        exit: {
          opacity: 0,
          y: -10,
          scale: 0.99
        }
      };
  }
};